import { useState, useEffect } from 'react'
import { Tooltip, Table, Spin, Button, Modal } from 'antd'
import '@styles'
import { CloseOutlined, DownloadOutlined, FilterOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'

import { useGetBatchees, usePostBatchBilling, useUserResponseManager } from '@hooks'
import { useFilters, useGeneral, useUser } from '@contexts'
import { DataApi } from '@api'
import { Batch } from '@types'

import {
  ActionsCellContainer,
  ContentContainer,
  StyledRow,
  StyledSelect,
} from '../StyledComponents'
import sortBySetter from '../../utils/sort-by-setter'

interface BatchItem {
  carrier: { id: number; businessName: string }
  closeDate: string | null
  createAt: string
  hes: string
  hesDate: string
  id: number
  openDate: string
  status: string
  ticketCount: number
  updateAt: string
}

const OpenAdmin = () => {
  const { t } = useTranslation()
  const { filters, setFilters } = useFilters()
  const { filtersDisplayed, handleDisplayFilters, menuDisplayed } = useGeneral()
  const { billingBatch, isLoading } = useGetBatchees({ status: 'OPEN' })
  const openStatusObjects: Batch[] = billingBatch.filter((item: Batch) => item.status === 'OPEN')
  const [filteredData, setFilteredData] = useState<BatchItem[]>(openStatusObjects as BatchItem[])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [batchIdToClose, setBatchIdToClose] = useState<number>(0)
  const [batchCarrierToClose, setBatchCarrierToClose] = useState(0)
  const [nameBatchCarrierToClose, setNameBatchCarrierToClose] = useState('')
  const { closeBatch } = usePostBatchBilling()
  const [selectedCarriers, setSelectedCarriers] = useState<string[]>([])
  const [uniqueCarrierNames, setUniqueCarrierNames] = useState<string[]>([])
  const { state: user } = useUser()
  const { getUser } = useUserResponseManager(user?.id)

  const handleCarrierNameSearch = (selectedValues: any) => {
    setSelectedCarriers(selectedValues as string[])
    filterByCarrierNames(selectedValues as string[])
  }

  const filterByCarrierNames = (selectedValues: string[]) => {
    const data = openStatusObjects.filter(
      (item: Batch) =>
        selectedValues.length === 0 ||
        selectedValues.some(
          value => item.carrier.businessName.toLowerCase() === value.toLowerCase(),
        ),
    ) as BatchItem[]
    setFilteredData(data)
  }

  useEffect(() => {
    if (!isLoading) {
      const uniqueNamesSet = new Set(billingBatch.map(batch => batch.carrier.businessName))
      const uniqueNamesArray = Array.from(uniqueNamesSet)
      setUniqueCarrierNames(uniqueNamesArray)

      const newFilteredData = billingBatch.filter(
        (item: Batch) => item.status === 'OPEN',
      ) as BatchItem[]
      setFilteredData(newFilteredData)
    }
  }, [billingBatch, isLoading])

  const handleDownloadExcel = (id: number, businessName: string) => {
    DataApi.getBillingBatchExcel(id).then(res => {
      const link = document.createElement('a')
      const url = window.URL.createObjectURL(res)
      link.href = url
      document.body.appendChild(link)
      link.setAttribute('download', `Lote-${id}-${businessName}.xlsx`)
      link.click()
      link.parentNode?.removeChild(link)
    })
  }

  const handleCloseBatch = (id: number, carrier: { id: number; businessName: string }) => {
    setBatchCarrierToClose(carrier?.id)
    setBatchIdToClose(id)
    setNameBatchCarrierToClose(carrier?.businessName)
    setIsModalVisible(true)
  }

  const closeBatchModal = () => {
    setIsModalVisible(false)
    closeBatch(batchCarrierToClose)
  }

  const columnsBatch: ColumnsType = [
    {
      title: 'ID Lote',
      dataIndex: 'id',
      key: 'id',
      sorter: sortBySetter('id'),
      sortDirections: ['ascend'],
    },
    {
      title: 'Transportista',
      dataIndex: ['carrier', 'businessName'],
      key: 'carrierName',
    },
    {
      title: 'Lote #',
      dataIndex: 'carrierSeq',
      key: 'carrierSeq',
    },
    {
      title: 'Desde',
      dataIndex: 'createAt',
      key: 'createAt',
      render: (field: Date) => (field ? dayjs(field).format('YYYY/MM/DD HH:mm') : '-'),
      sorter: (a, b) => (dayjs(b.createAt).isAfter(dayjs(a.createAt)) ? 1 : -1),
    },
    {
      title: 'Hasta',
      dataIndex: 'closeDate',
      key: 'closeDate',
      render: (field: Date) => (field ? dayjs(field).format('YYYY/MM/DD HH:mm') : '-'),
      sorter: (a, b) => (dayjs(b.closeDate).isAfter(dayjs(a.closeDate)) ? 1 : -1),
    },
    {
      title: 'Viajes',
      dataIndex: 'ticketCount',
      key: 'ticketCount',
      render: (ticketCount: number) => <span>{ticketCount ?? 0}</span>,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      align: 'center',
      render: (record: { id: number; carrier: { id: number; businessName: string } }) =>
        getUser?.user.roles[0].name === 'ADMINRO' ? (
          <ActionsCellContainer>
            <Tooltip title={t('admin_action.download_excel')} placement="left">
              <div style={{ marginLeft: '20px' }}>
                <DownloadOutlined
                  className="update"
                  onClick={() => {
                    handleDownloadExcel(record.id, record.carrier.businessName)
                  }}
                />
              </div>
            </Tooltip>
          </ActionsCellContainer>
        ) : (
          <ActionsCellContainer>
            <Tooltip title={t('admin_action.download_excel')} placement="left">
              <div style={{ marginLeft: '20px' }}>
                <DownloadOutlined
                  className="update"
                  onClick={() => {
                    handleDownloadExcel(record.id, record.carrier.businessName)
                  }}
                />
              </div>
            </Tooltip>
            <Tooltip title={t('admin_action.close_bach')} placement="left">
              <div style={{ marginLeft: '20px' }}>
                <CloseOutlined
                  className="update"
                  onClick={() => {
                    handleCloseBatch(record.id, record.carrier)
                  }}
                />
              </div>
            </Tooltip>
          </ActionsCellContainer>
        ),
    },
  ]

  if (isLoading) {
    return (
      <div className="centered-spinner">
        <Spin size="default" />
      </div>
    )
  }
  return (
    <ContentContainer>
      <StyledRow className="sticky-options">
        <div
          className={`filters-container${
            filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
          }`}
        >
          <Button onClick={handleDisplayFilters} id="displayFiltersButton">
            <FilterOutlined />
          </Button>
          <div className="filters-list">
            <StyledSelect
              maxTagCount="responsive"
              mode="multiple"
              allowClear
              placeholder={t('admin_action.search_by_TTE')}
              value={selectedCarriers}
              onChange={handleCarrierNameSearch}
            >
              {uniqueCarrierNames.map((carrierName: string) => (
                <StyledSelect.Option key={carrierName} value={carrierName}>
                  {carrierName}
                </StyledSelect.Option>
              ))}
            </StyledSelect>
          </div>
        </div>
      </StyledRow>
      <>
        <StyledRow className="ticket-handler">
          <div style={{ flex: 1 }} />
          <div style={{ flex: 1 }} />
        </StyledRow>
        <Table
          className="stripped-table"
          columns={columnsBatch}
          dataSource={filteredData}
          rowKey="id"
          pagination={{
            pageSize: 10,
            onChange: (page, pageSize) => {
              setFilters({
                ...filters,
                limit: pageSize ?? 5,
                page,
              })
            },
          }}
        />
      </>
      <div>
        <Modal
          title="Confirmar Cierre del Lote"
          open={isModalVisible}
          onOk={closeBatchModal}
          onCancel={() => setIsModalVisible(false)}
        >
          <p>
            ¿Quieres cerrar el lote {`${batchIdToClose}`} del {`${nameBatchCarrierToClose}`}?
          </p>
        </Modal>
      </div>
    </ContentContainer>
  )
}

export { OpenAdmin }
